import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  ChatGPTIcon,
  ClaudeIcon,
  CursorIcon,
  GeminiIcon,
  LovableIcon,
} from '@slite/icons'

import { getCustomerQuote } from '../../../components/customerQuotes'
import { SectionCurve } from '../../../components/SectionCurve'
import { SectionFAQ } from '../../../components/SectionFAQ'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { SectionTestimonials } from '../../home/SectionTestimonials'
import { Section3CardsBenefits } from '../Section3CardsBenefits'
import { SectionGridBenefits } from '../SectionGridBenefits'
import { SectionHero } from '../SectionHero'
import { SectionQuote } from '../SectionQuote'
import { SectionSwitchedBenefits } from '../SectionSwitchedBenefits'
import { SectionTable } from '../SectionTable'
import { SectionTwoColumns } from '../SectionTwoColumns'

const AI_TOOLS = [
  { Icon: ClaudeIcon, name: 'Claude' },
  { Icon: ChatGPTIcon, name: 'ChatGPT' },
  { Icon: CursorIcon, name: 'Cursor' },
  { Icon: GeminiIcon, name: 'Gemini' },
  { Icon: LovableIcon, name: 'Lovable' },
]

export default function CompareToSlabPage() {
  const routes = useRoutes()
  const intl = useIntl()
  const alexisQuote = getCustomerQuote('AlexisDupontAgorapulseQuestions', intl)

  const title = intl.formatMessage({
    defaultMessage:
      'Slite - Best Slab alternative for team knowledge management',
    id: 'compare.slab.page.seo.title',
  })

  const description = intl.formatMessage({
    defaultMessage:
      'Teams switch from Slab to Slite for knowledge that stays accurate, AI that works on every plan, and Super to plug their entire company into the agent era.',
    id: 'compare.slab.page.seo.description',
  })

  return (
    <SlitePage
      canonicalUrl={routes.compare.detail({ slug: 'alternative-to-slab' })}
      background="moon-dust"
      title={title}
      description={description}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/04faad7d7e45f2c8d501e81655fdc147812b94a1-1200x675.jpg"
    >
      <SectionHero
        competitorName="Slab"
        title={intl.formatMessage(
          {
            defaultMessage:
              'Slab got you started.{br}But knowledge management has moved on - and so have your AI tools.',
            id: 'compare.slab.page.title',
          },
          { br: <br /> }
        )}
        subtitle={intl.formatMessage({
          defaultMessage:
            'Teams switch to Slite for knowledge that stays accurate, AI that works on every plan, and our enterprise search Super to plug their entire company into the agent era.',
          id: 'compare.slab.page.subtitle',
        })}
        background="moon-dust"
      />

      <SectionTwoColumns
        background="moon-dust"
        leftTitle={intl.formatMessage({
          id: 'compare.slab.youGetThis.kept.title',
          defaultMessage: 'Everything you loved about Slab...',
        })}
        leftItems={[
          intl.formatMessage({
            id: 'compare.slab.youGetThis.kept.3',
            defaultMessage: 'One central place for all team knowledge',
          }),
          intl.formatMessage({
            id: 'compare.slab.youGetThis.kept.1',
            defaultMessage: 'Focused on verified knowledge and ease of use',
          }),
          intl.formatMessage({
            id: 'compare.slab.youGetThis.kept.2',
            defaultMessage: 'Powered by a fast, unified search',
          }),
        ]}
        rightTitle={intl.formatMessage({
          id: 'compare.slab.youGetThis.plus.title',
          defaultMessage: '... Ready for the AI era',
        })}
        rightItems={[
          {
            label: intl.formatMessage({
              id: 'compare.slab.youGetThis.plus.1',
              defaultMessage:
                'AI answers and writing assistance on every paid plan',
            }),
            iconShape: 'sparkles',
            iconColor: 'purple',
          },
          {
            label: intl.formatMessage({
              id: 'compare.slab.youGetThis.plus.2',
              defaultMessage:
                'World class MCP and API - feed your wiki to all your agents and update it remotely',
            }),
            iconShape: 'electric_plug',
            iconColor: 'blue',
          },
          {
            label: intl.formatMessage({
              id: 'compare.slab.youGetThis.plus.3',
              defaultMessage:
                'Enterprise search - get answer across all your tools',
            }),
            iconShape: 'mag',
            iconColor: 'turquoise',
          },
        ]}
      />

      <SectionCurve from="moon-dust" to="shade-100" shape="top-large" />
      {/* Section 1: Agent-ready knowledge base + MCP cards */}
      <Section3CardsBenefits
        title={intl.formatMessage({
          defaultMessage: 'Your team needs an agent-ready knowledge base.',
          id: 'compare.slab.mcpSection.title',
        })}
        subtitle={
          <>
            <WebsiteText variant="body-lg" className="margin-bottom-32">
              {intl.formatMessage({
                defaultMessage:
                  'Your knowledge base needs to feed accurate context to every AI tool your team uses - and be maintained from anywhere. Slab has no MCP, no agent story, and gates AI behind its most expensive plan.',
                id: 'compare.slab.mcpSection.subtitle',
              })}
            </WebsiteText>
            <div className="margin-bottom-32 flexbox flexbox-centered flexbox-gap-8 shade-20">
              <WebsiteText variant="body-lg">
                <FormattedMessage
                  id="super.mcp.hero.workswith"
                  defaultMessage="Works with"
                />
              </WebsiteText>
              <div className="flexbox flexbox-align-center flexbox-gap-12">
                {AI_TOOLS.map(({ Icon, name }) => (
                  <Icon key={name} size={32} />
                ))}
              </div>
            </div>
          </>
        }
        benefits={[
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/93e52263d67eadb7228aaff90e65f823bcaf0b27-1136x1137.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Slite MCP tools illustration',
              id: 'compare.slab.mcpCard1.image.alt',
            }),
            imageBackground: 'transparent',
            title: intl.formatMessage({
              defaultMessage: 'Not just search - full control via MCP.',
              id: 'compare.slab.mcpCard1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "30+ tools: search, create, edit, verify, archive, manage your knowledge health. Your AI tools don't just read your wiki - they write to it. Slab has zero MCP.",
              id: 'compare.slab.mcpCard1.description',
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/04d6180959476e0b3bd857bcddf8793e3335ff5f-1136x1137.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Super MCP cross-tool search illustration',
              id: 'compare.slab.mcpCard2.image.alt',
            }),
            imageBackground: 'transparent',
            title: intl.formatMessage({
              defaultMessage: 'Works everywhere your team works.',
              id: 'compare.slab.mcpCard2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Claude, ChatGPT, Cursor, Gemini, Lovable - and any MCP-compatible AI. Plus Super MCP to search across 20+ sources: Slack, Linear, Jira, Google Drive, GitHub, and more. One search, one answer from your entire company.',
              id: 'compare.slab.mcpCard2.description',
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/3b0282ca21d1fabbab02edc6b3054704ce81655b-1136x1137.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Slite Agent self-healing knowledge illustration',
              id: 'compare.slab.mcpCard3.image.alt',
            }),
            imageBackground: 'transparent',
            title: intl.formatMessage({
              defaultMessage: 'The first self-healing knowledge base.',
              id: 'compare.slab.mcpCard3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'The Slite Agent will maintain your docs autonomously - structure, rename, clean up, verify - in bulk, faster than any human. All changes are reviewable before applying. Just tell it what to do.',
              id: 'compare.slab.mcpCard3.description',
            }),
          },
        ]}
      />
      {/* Section 2: Comparison table */}
      <SectionCurve from="shade-100" to="shade-00" shape="top-large" />
      <SectionTable
        competitorName="Slab"
        background="shade-00"
        title={intl.formatMessage({
          defaultMessage: "Slab hasn't shipped meaningful updates in years.",
          id: 'compare.slab.comparisonTable.title',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'No MCP, no enterprise search, and AI locked behind the most expensive plan. Slite gives you all of that on every paid plan.',
          id: 'compare.slab.comparisonTable.description.v2',
        })}
        checkedFeatures={['company-wiki-templates', 'document-verification']}
      />
      <SectionCurve
        from="shade-100"
        to="shade-00"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />
      {/* Section 3: Super AI search */}
      <SectionGridBenefits
        background="shade-100"
        title={intl.formatMessage({
          defaultMessage:
            'Get instant trusted answers, where and when you need them.',
          id: 'compare.slab.superSearchSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage: 'Can I really just ask instead of search?',
              id: 'compare.slab.superSearch.benefit1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Endless searching and browsing is a thing of the past. With Super, just ask questions in natural language and get instant answers sourced from your docs, Slack threads, tickets, and 20+ connected tools. Slab only searches Slab.',
              id: 'compare.slab.superSearch.benefit1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/7026c497c3a256414ba10a092aec0e19ba328dba-1072x665.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'AI-powered instant answers illustration',
              id: 'compare.slab.superSearch.benefit1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'Can I get answers where I already work?',
              id: 'compare.slab.superSearch.benefit2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'No more context switching - stay in your workflow and get trusted answers in Slack, while you browse with the Chrome extension, or via MCP in Claude and ChatGPT. Teams using Super see up to 90% fewer internal questions.',
              id: 'compare.slab.superSearch.benefit2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/9f2ce8d23d5caafbedcacc0ececb057ffd4ba0ff-2144x1328.png',
            imageClassnames:
              'padding-horizontal-24 margin-bottom-24 radius-larger overflow-hidden',
            imageAlt: intl.formatMessage({
              defaultMessage:
                'Super answering questions in Slack and Chrome extension',
              id: 'compare.slab.superSearch.benefit2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'How do I know if I can trust the answers?',
              id: 'compare.slab.superSearch.benefit3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Every answer cites its sources with direct links. Super scores 89/100 on accuracy benchmarks - vs 62 for Guru and 70 for Notion AI. It respects your team permissions and can filter answers to verified sources only.',
              id: 'compare.slab.superSearch.benefit3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/064bfb97c2427edeaf91146ae0bd1957bbb297a2-2144x1748.png',
            imageAlt: intl.formatMessage({
              defaultMessage:
                'Trusted answers with verified sources illustration',
              id: 'compare.slab.superSearch.benefit3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'But knowledge is scattered across all our tools...',
              id: 'compare.slab.superSearch.benefit4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "Exactly - your company knowledge is so much more than what's in your wiki. Super connects Slack, Google Drive, Linear, Jira, GitHub, Notion, Confluence, Intercom, HubSpot, and more. One answer from your entire company, not just one tool.",
              id: 'compare.slab.superSearch.benefit4.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/569a7178bcccf8f49b9775eae3c5e4a6fe7e1a78-1072x665.png',
            imageClassnames:
              'padding-horizontal-24 flexbox flexbox-justify-end',
            imageAlt: intl.formatMessage({
              defaultMessage:
                'Cross-tool search connecting all company sources',
              id: 'compare.slab.superSearch.benefit4.image.alt',
            }),
          },
        ]}
      />
      <SectionCurve from="shade-100" to="primary" shape="top-large" />
      <SectionQuote
        background="primary"
        quote={`"${alexisQuote.testimonial}"`}
        authorImageUrl={alexisQuote.pictureUrl}
        authorTitle={alexisQuote.name}
        authorDescription={alexisQuote.title}
      />
      <SectionCurve
        from="shade-100"
        to="primary"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />
      {/* Sounds great, but I have a few questions */}
      <SectionSwitchedBenefits
        background="shade-100"
        title={intl.formatMessage({
          defaultMessage: 'Sounds great, but I have a few questions...',
          id: 'compare.slab.faqSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage: "Slab doesn't give us AI. Does Slite?",
              id: 'compare.slab.faq1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Slab locks AI behind its most expensive Business plan. Slite includes AI answers, writing assistance, and AI-suggested actions on every paid plan - starting at $8/user/month. You can also restrict answers from verified docs, so your team can trust what they get.',
              id: 'compare.slab.faq1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/73407b8a80d3ff5231cf70073be4fb9af9089d79-1614x1200.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'AI pricing comparison illustration',
              id: 'compare.slab.faq1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "It's impossible to manually find and update all our docs.",
              id: 'compare.slab.faq2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'We find them for you. The Knowledge Management Panel lets you slice and dice all your docs, take AI-suggested actions and act on them in bulk - all in one central place. Spot inactive docs, expired verifications, and empty pages at a glance.',
              id: 'compare.slab.faq2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/1aa50d8a526972c03ba9ece421d3038fa1d818b6-1375x864.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Knowledge Management Panel illustration',
              id: 'compare.slab.faq2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'Will non-technical teams actually use it?',
              id: 'compare.slab.faq3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Slite is powerful enough for engineering but intuitive enough for HR, support, and ops. Teams switching from Slab consistently say Slite is easier to adopt company-wide - no more siloed teams using different tools.',
              id: 'compare.slab.faq3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/fa51bf7dd0c15400b6e544db4d541517284b91be-1396x723.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Easy adoption illustration',
              id: 'compare.slab.faq3.image.alt',
            }),
          },
        ]}
      />
      <SectionCurve from="shade-100" to="moon-dust" shape="top-large" />
      <SectionTestimonials withCaseStudies={false} />
      <SectionFAQ
        className="margin-bottom-60 padding-bottom-60"
        background="moon-dust"
        faq={[
          {
            question: intl.formatMessage({
              defaultMessage: 'How does Slab pricing compare to Slite?',
              id: 'compare.slab.faqAccordion.q1.question',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                'Slab offers a free plan for up to 10 users with limited features. Paid plans start at $6.67/user/month (billed annually) for the Startup plan, and AI features are only available on the Business plan and above. Slite starts at $8/user/month and includes AI search and answers (Ask) on every paid plan — no premium tier required.',
              id: 'compare.slab.faqAccordion.q1.answer',
            }),
          },
          {
            question: intl.formatMessage({
              defaultMessage:
                "What's the best Slab alternative for small teams?",
              id: 'compare.slab.faqAccordion.q2.question',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                "Slite is one of the top alternatives to Slab for small and growing teams. It offers a clean, intuitive interface that teams adopt quickly — even non-technical members — along with AI-powered search, document verification, and knowledge management tools. Unlike Slab, Slite includes AI on every plan, so you don't need to upgrade to access the features that matter most.",
              id: 'compare.slab.faqAccordion.q2.answer',
            }),
          },
          {
            question: intl.formatMessage({
              defaultMessage: 'Can I migrate my content from Slab to Slite?',
              id: 'compare.slab.faqAccordion.q3.question',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                "Yes. You can export your Slab content as Markdown files and import them directly into Slite. Links and callout blocks are preserved during the import. For larger teams, Slite's Enterprise plan includes migration support. If you have questions about the process, reach out to support@slite.com.",
              id: 'compare.slab.faqAccordion.q3.answer',
            }),
          },
          {
            question: intl.formatMessage({
              defaultMessage: 'Does Slite work for non-technical teams?',
              id: 'compare.slab.faqAccordion.q4.question',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                'Absolutely. Slite is designed for company-wide adoption, not just technical users. Its minimalist editor, intuitive navigation, and AI-powered search make it easy for anyone to find and contribute to the knowledge base — no training required. Teams switching from Slab consistently report a shorter learning curve and higher knowledge base adoption with Slite.',
              id: 'compare.slab.faqAccordion.q4.answer',
            }),
          },
        ]}
      />
      <SectionLastCTA background="moon-dust" competitorName="Slab" />
    </SlitePage>
  )
}
